<template>
  <div class="user-activity-map-view">
    <b-card class="mb-1">
      <b-row class="align-items-end">
        <b-col
          cols="12"
          md="3"
          class="mb-1 mb-md-0"
        >
          <h4 class="mb-0">
            خريطة نشاط المستخدمين
          </h4>
          <small
            v-if="meta.filtered_from"
            class="text-muted"
          >
            عرض النشاط منذ {{ formatFilteredFrom(meta.filtered_from) }}
            <template v-if="meta.history && meta.history_days">
              (آخر {{ meta.history_days }} {{ meta.history_days === 1 ? 'يوم' : 'أيام' }})
            </template>
          </small>
        </b-col>
        <b-col
          cols="12"
          md="3"
          class="mb-1 mb-md-0"
        >
          <label class="form-label mb-0">{{ timeFilterLabel }}</label>
          <b-form-select
            v-if="historicalData"
            v-model="historyDays"
            :options="historyDaysOptions"
            size="sm"
            :disabled="loading"
            @change="onFiltersChange"
          />
          <b-form-select
            v-else
            v-model="sinceMinutes"
            :options="sinceOptions"
            size="sm"
            :disabled="loading"
            @change="onFiltersChange"
          />
        </b-col>
        <b-col
          cols="12"
          md="3"
          class="mb-1 mb-md-0"
        >
          <label class="form-label mb-0">المستخدم (اختياري)</label>
          <v-select
            v-model="selectedUser"
            :options="userOptions"
            label="label"
            placeholder="جميع المستخدمين"
            :clearable="true"
            :loading="usersLoading"
            dir="rtl"
            @input="onUserFilterChange"
          />
        </b-col>
        <b-col
          cols="12"
          md="2"
          class="text-md-left"
        >
          <b-form-checkbox
            v-model="historicalData"
            :disabled="loading"
            switch
            class="mt-md-2"
            @change="onHistoricalDataChange"
          >
            عرض البيانات التاريخية
          </b-form-checkbox>
        </b-col>
        <b-col
          cols="12"
          md="2"
          class="text-md-left"
        >
          <b-button
            variant="outline-primary"
            size="sm"
            :disabled="loading"
            @click="fetchPositions"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="14"
              :class="{ 'spin': loading }"
            />
            تحديث
          </b-button>
        </b-col>
      </b-row>

      <b-row class="mt-1">
        <b-col cols="12">
          <div class="d-flex flex-wrap align-items-center">
            <b-badge
              variant="light-primary"
              class="mr-1 mb-50"
            >
              {{ positionCountLabel }}: {{ positionRowCount }}
            </b-badge>
            <b-badge
              variant="light-primary"
              class="mr-1 mb-50"
            >
              {{ uniqueUserCountLabel }}: {{ uniqueUserCount }}
            </b-badge>
            <b-badge
              v-if="isHistoryMode"
              variant="light-warning"
              class="mr-1 mb-50"
            >
              مسار GPS للنشاط
            </b-badge>
            <b-badge
              v-else
              variant="light-warning"
              class="mr-1 mb-50"
            >
              آخر موقع لكل مستخدم وعنوان IP
            </b-badge>
            <b-badge
              v-if="lastRefreshedAt"
              variant="light-secondary"
              class="mr-1 mb-50"
            >
              آخر تحديث: {{ formatRelativeTime(lastRefreshedAt) }}
            </b-badge>
            <b-badge
              v-if="!isHistoryMode"
              variant="light-info"
              class="mb-50"
            >
              تحديث تلقائي كل {{ pollIntervalSeconds }} ثانية
            </b-badge>
          </div>
        </b-col>
      </b-row>
    </b-card>

    <b-alert
      v-if="errorMessage"
      variant="danger"
      show
      dismissible
      class="mb-1"
      @dismissed="clearErrorMessage"
    >
      {{ errorMessage }}
    </b-alert>

    <div class="map-container-wrapper mb-1">
      <div
        v-if="mapLoading"
        class="loading-overlay"
      >
        <b-spinner variant="light" />
        <p class="text-white mt-2 mb-0">
          جاري تحميل الخريطة...
        </p>
      </div>
      <div
        ref="leafletMap"
        class="activity-leaflet-map"
      ></div>
      <div class="map-legend">
        <div class="legend-title">
          حداثة النشاط
        </div>
        <div
          v-for="item in legendItems"
          :key="item.label"
          class="legend-item"
        >
          <span
            class="legend-dot"
            :style="{ backgroundColor: item.color }"
          ></span>
          <span>{{ item.label }}</span>
        </div>
      </div>
    </div>

    <b-card>
      <b-alert
        v-if="!loading && positions.length === 0 && !errorMessage"
        variant="info"
        show
      >
        {{ emptyStateMessage }}
        <hr>
        <small class="text-muted">
          يتطلب ظهور المستخدم على الخريطة إرسال إحداثيات الموقع (X-Latitude و X-Longitude) مع طلبات API.
          تأكد من السماح بالوصول إلى الموقع في المتصفح.
        </small>
      </b-alert>
      <div
        v-else-if="positions.length > 0"
        class="positions-summary"
      >
        <h6 class="mb-50">
          {{ tableTitle }}
          <small class="text-muted font-weight-normal">
            ({{ tableSubtitle }})
          </small>
        </h6>
        <b-table
          :items="positionTableItems"
          :fields="positionTableFields"
          primary-key="_positionKey"
          small
          responsive
          hover
          striped
          tbody-tr-class="cursor-pointer"
          @row-clicked="focusMarker"
        >
          <template #cell(user_name)="{ item }">
            {{ getPositionUserName(item) }}
          </template>
          <template #cell(ip_address)="{ item }">
            <code class="ip-address-cell">{{ item.ip_address }}</code>
          </template>
          <template #cell(last_action)="{ item }">
            {{ getActionLabel(item.last_action) }}
          </template>
          <template #cell(action_at)="{ item }">
            <b-badge :variant="getBadgeVariant(item.action_at)">
              {{ formatRelativeTime(item.action_at) }}
            </b-badge>
          </template>
        </b-table>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormSelect,
  BFormCheckbox,
  BButton,
  BSpinner,
  BAlert,
  BBadge,
  BTable,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import activityLogsApi from '@/api/activity-logs/activityLogs'
import {
  getUserLastActivityPositionKey,
  countUniqueUsersFromPositions,
  sortPositionsByActionAtAsc,
  sortPositionsByActionAtDesc,
  groupPositionsByUserAndIp,
} from '@/api/activity-logs/types'
import User from '@/api/user_settings/users/user'
import { loadLeafletAssets } from '@/utils/leafletLoader'

/** Display size on map (source PNG is 128×128) */
const LOCATOR_ICON_WIDTH = 24
const LOCATOR_ICON_HEIGHT = 24
const USER_LOCATOR_ICON_SIZE = 28
const LOCATOR_ICON_URL = require('@/assets/maps/locatorIcon.png')
const SELF_LOCATOR_COLOR = '#0066ff'
const MARKER_COLORS = ['#28a745', '#ffc107', '#fd7e14', '#dc3545', SELF_LOCATOR_COLOR]

const tintedLocatorIconUrls = {}
let locatorImagePromise = null

function loadLocatorImageElement() {
  if (!locatorImagePromise) {
    locatorImagePromise = new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = reject
      img.src = LOCATOR_ICON_URL
    })
  }
  return locatorImagePromise
}

function tintLocatorIcon(colorHex) {
  if (tintedLocatorIconUrls[colorHex]) {
    return Promise.resolve(tintedLocatorIconUrls[colorHex])
  }

  return loadLocatorImageElement().then(img => {
    const canvas = document.createElement('canvas')
    canvas.width = img.naturalWidth || img.width
    canvas.height = img.naturalHeight || img.height
    const ctx = canvas.getContext('2d')
    ctx.drawImage(img, 0, 0)
    ctx.globalCompositeOperation = 'source-in'
    ctx.fillStyle = colorHex
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    const url = canvas.toDataURL('image/png')
    tintedLocatorIconUrls[colorHex] = url
    return url
  })
}

function preloadLocatorIcons() {
  return Promise.all(MARKER_COLORS.map(tintLocatorIcon))
}

function getLocatorIconUrl(colorHex) {
  return tintedLocatorIconUrls[colorHex] || LOCATOR_ICON_URL
}

const SINCE_OPTIONS = [
  { text: '10 دقائق', value: 10 },
  { text: '15 دقيقة', value: 15 },
  { text: '20 دقيقة', value: 20 },
  { text: '30 دقيقة', value: 30 },
  { text: 'ساعة واحدة', value: 60 },
]

const HISTORY_DAYS_OPTIONS = [
  { text: 'يوم واحد', value: 1 },
  { text: '3 أيام', value: 3 },
  { text: '7 أيام', value: 7 },
  { text: '10 أيام', value: 10 },
  { text: '14 يوماً', value: 14 },
  { text: '30 يوماً', value: 30 },
]

const POLL_INTERVAL_MS = 45000

const userApi = new User()

export default {
  name: 'UserActivityMap',
  components: {
    BCard,
    BRow,
    BCol,
    BFormSelect,
    BFormCheckbox,
    BButton,
    BSpinner,
    BAlert,
    BBadge,
    BTable,
    vSelect,
    FeatherIcon,
  },
  data() {
    return {
      sinceMinutes: 30,
      sinceOptions: SINCE_OPTIONS,
      historyDays: 10,
      historyDaysOptions: HISTORY_DAYS_OPTIONS,
      historicalData: false,
      selectedUser: null,
      userOptions: [],
      usersLoading: false,
      positions: [],
      meta: {
        total: 0,
        since_minutes: null,
        history_days: null,
        filtered_from: null,
        history: false,
      },
      loading: false,
      mapLoading: true,
      errorMessage: null,
      lastRefreshedAt: null,
      pollTimer: null,
      pollIntervalSeconds: POLL_INTERVAL_MS / 1000,
      map: null,
      markersLayer: null,
      userLocationLayer: null,
      leafletLib: null,
      markerByPositionKey: {},
      defaultCenter: [30.0444, 31.2357],
    }
  },
  computed: {
    legendItems() {
      return [
        { label: 'أقل من 10 دقائق', color: '#28a745' },
        { label: '10–20 دقيقة', color: '#ffc107' },
        { label: '20–30 دقيقة', color: '#fd7e14' },
        { label: 'أكثر من 30 دقيقة', color: '#dc3545' },
      ]
    },
    timeFilterLabel() {
      return this.historicalData ? 'فترة السجل التاريخي' : 'الفترة الزمنية'
    },
    isHistoryMode() {
      return !!(this.meta.history || this.historicalData)
    },
    positionCountLabel() {
      return this.isHistoryMode ? 'سجلات GPS' : 'مواقع النشاط'
    },
    uniqueUserCountLabel() {
      return this.isHistoryMode ? 'مستخدمون (فريدون)' : 'مستخدمون نشطون'
    },
    tableTitle() {
      return this.isHistoryMode ? 'سجل حركة GPS' : 'المواقع على الخريطة'
    },
    tableSubtitle() {
      return this.isHistoryMode
        ? 'جميع نقاط النشاط مرتبة من الأحدث'
        : 'آخر موقع لكل مستخدم وعنوان IP'
    },
    emptyStateMessage() {
      if (this.isHistoryMode) {
        const option = HISTORY_DAYS_OPTIONS.find(o => o.value === this.historyDays)
        const label = option ? option.text : `${this.historyDays} أيام`
        return `لا توجد سجلات GPS للنشاط خلال آخر ${label}.`
      }
      const option = SINCE_OPTIONS.find(o => o.value === this.sinceMinutes)
      const label = option ? option.text : `${this.sinceMinutes} دقيقة`
      return `لا يوجد نشاط بإحداثيات موقع (لكل مستخدم وعنوان IP) خلال آخر ${label}.`
    },
    positionRowCount() {
      return (this.meta.total != null) ? this.meta.total : this.positions.length
    },
    uniqueUserCount() {
      return countUniqueUsersFromPositions(this.positions)
    },
    positionTableFields() {
      return [
        { key: 'user_name', label: 'المستخدم' },
        { key: 'ip_address', label: 'عنوان IP' },
        { key: 'last_action', label: 'آخر إجراء' },
        { key: 'action_at', label: 'الوقت' },
      ]
    },
    positionTableItems() {
      const sorted = this.isHistoryMode
        ? sortPositionsByActionAtDesc(this.positions)
        : this.positions

      return sorted.map(position => ({
        ...position,
        _positionKey: getUserLastActivityPositionKey(position, this.isHistoryMode),
      }))
    },
  },
  async mounted() {
    await this.initMap()
    try {
      await preloadLocatorIcons()
    } catch (error) {
      console.warn('Could not prepare locator map icons:', error)
    }
    await Promise.all([
      this.loadUserOptions(),
      this.fetchPositions(),
    ])
    this.startPolling()
    this.requestGeolocationPermission()
  },
  beforeDestroy() {
    this.stopPolling()
    if (this.map) {
      this.map.remove()
      this.map = null
    }
    this.leafletLib = null
  },
  methods: {
    getL() {
      return this.leafletLib || window.L
    },
    async initMap() {
      try {
        const L = await loadLeafletAssets()
        this.leafletLib = L

        this.map = L.map(this.$refs.leafletMap, {
          zoomControl: true,
        }).setView(this.defaultCenter, 10)

        L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        }).addTo(this.map)

        this.markersLayer = L.layerGroup().addTo(this.map)
        this.userLocationLayer = L.layerGroup().addTo(this.map)

        this.$nextTick(() => {
          this.map.invalidateSize()
        })
      } catch (error) {
        console.error('Failed to init activity map:', error)
        this.errorMessage = 'تعذر تحميل الخريطة. تحقق من الاتصال بالإنترنت.'
      } finally {
        this.mapLoading = false
      }
    },
    requestGeolocationPermission() {
      if (!navigator.geolocation) return

      navigator.geolocation.getCurrentPosition(
        position => {
          const lat = position.coords.latitude
          const lng = position.coords.longitude
          this.addUserLocationMarker(lat, lng)
          if (this.map && this.positions.length === 0) {
            this.map.setView([lat, lng], 12)
          }
        },
        () => {},
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 },
      )
    },
    async loadUserOptions() {
      this.usersLoading = true
      try {
        const response = await userApi.list({ per_page: 500, page: 1 })
        const users = (response && response.data) ? response.data : []

        this.userOptions = (Array.isArray(users) ? users : []).map(user => ({
          id: user.id,
          label: user.name || user.email || `مستخدم #${user.id}`,
        }))
      } catch (error) {
        console.warn('Could not load users for filter:', error)
      } finally {
        this.usersLoading = false
      }
    },
    buildQueryParams() {
      const params = {}
      if (this.selectedUser && this.selectedUser.id) {
        params.user_id = this.selectedUser.id
      }
      if (this.historicalData) {
        params.history = true
        params.history_days = this.historyDays
      } else {
        params.since_minutes = this.sinceMinutes
      }
      return params
    },
    async fetchPositions() {
      if (!this.hasPermission('positions activity_logs')) {
        this.errorMessage = 'ليس لديك صلاحية عرض سجل النشاطات.'
        return
      }

      this.loading = true
      try {
        const response = await activityLogsApi.getLastPositions(this.buildQueryParams())

        if (response && response.status === 'success') {
          this.positions = response.data || []
          this.meta = {
            total: (response.meta && response.meta.total != null)
              ? response.meta.total
              : this.positions.length,
            since_minutes: (response.meta && response.meta.since_minutes != null)
              ? response.meta.since_minutes
              : null,
            history_days: (response.meta && response.meta.history_days != null)
              ? response.meta.history_days
              : null,
            filtered_from: (response.meta && response.meta.filtered_from)
              ? response.meta.filtered_from
              : null,
            history: !!(response.meta && response.meta.history),
          }
          if (this.meta.history_days != null) {
            this.historyDays = this.meta.history_days
          }
          if (this.meta.since_minutes != null) {
            this.sinceMinutes = this.meta.since_minutes
          }
          this.errorMessage = null
          this.lastRefreshedAt = new Date().toISOString()
          this.renderMarkers()
        } else {
          this.positions = (response && response.data) ? response.data : []
          this.renderMarkers()
        }
      } catch (error) {
        const status = error.response && error.response.status
        const message = (error.response && error.response.data && error.response.data.message)
          || error.message
          || 'حدث خطأ أثناء تحميل مواقع المستخدمين'

        if (status === 422) {
          this.errorMessage = message
        } else {
          this.errorMessage = message
        }
        this.positions = []
        this.renderMarkers()
      } finally {
        this.loading = false
      }
    },
    renderMarkers() {
      const L = this.getL()
      if (!L || !this.markersLayer) return

      this.markersLayer.clearLayers()
      this.markerByPositionKey = {}

      const validPositions = this.positions.filter(
        p => p.latitude != null
          && p.longitude != null
          && p.ip_address,
      )

      if (this.isHistoryMode) {
        this.renderHistoryTrails(L, validPositions)
      } else {
        this.renderLatestMarkers(L, validPositions)
      }

      if (validPositions.length > 0) {
        const bounds = L.latLngBounds(
          validPositions.map(p => [Number(p.latitude), Number(p.longitude)]),
        )
        this.map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 })
      } else if (this.userLocationLayer) {
        const userMarkers = this.userLocationLayer.getLayers()
        if (userMarkers.length > 0) {
          this.map.setView(userMarkers[0].getLatLng(), 12)
        }
      }
    },
    renderLatestMarkers(L, validPositions) {
      validPositions.forEach(position => {
        this.addPositionMarker(L, position, {
          history: false,
          pointIndex: null,
          totalPoints: null,
        })
      })
    },
    renderHistoryTrails(L, validPositions) {
      const groups = groupPositionsByUserAndIp(validPositions)

      Object.values(groups).forEach(group => {
        const trail = sortPositionsByActionAtAsc(group)

        if (trail.length >= 2) {
          const latestPoint = trail[trail.length - 1]
          const color = this.getMarkerColor(latestPoint.action_at)
          L.polyline(
            trail.map(p => [Number(p.latitude), Number(p.longitude)]),
            { color, weight: 3, opacity: 0.75 },
          ).addTo(this.markersLayer)
        }

        trail.forEach((position, index) => {
          this.addPositionMarker(L, position, {
            history: true,
            pointIndex: index + 1,
            totalPoints: trail.length,
          })
        })
      })
    },
    addPositionMarker(L, position, { history, pointIndex, totalPoints }) {
      const color = this.getMarkerColor(position.action_at)
      const marker = L.marker(
        [Number(position.latitude), Number(position.longitude)],
        { icon: this.createLocationPinIcon(color) },
      )

      const userName = this.getPositionUserName(position)
      const actionLabel = this.getActionLabel(position.last_action)
      const relativeTime = this.formatRelativeTime(position.action_at)
      const trailInfo = history && totalPoints > 1
        ? `<span>نقطة ${pointIndex} من ${totalPoints}</span><br>`
        : ''

      marker.bindPopup(`
        <div class="activity-marker-popup" dir="rtl">
          <strong>${this.escapeHtml(userName)}</strong><br>
          <span>عنوان IP: ${this.escapeHtml(position.ip_address)}</span><br>
          ${trailInfo}
          <span>${this.escapeHtml(actionLabel)}</span><br>
          <small>${this.escapeHtml(position.description || '')}</small><br>
          <em>${this.escapeHtml(relativeTime)}</em>
        </div>
      `)

      marker.addTo(this.markersLayer)
      this.markerByPositionKey[this.getPositionKey(position)] = marker
    },
    focusMarker(position) {
      const marker = this.markerByPositionKey[this.getPositionKey(position)]
      if (!marker || !this.map) return

      this.map.setView(marker.getLatLng(), Math.max(this.map.getZoom(), 14))
      marker.openPopup()
    },
    getPositionKey(position) {
      return getUserLastActivityPositionKey(position, this.isHistoryMode)
    },
    createLocationPinIcon(colorHex) {
      const L = this.getL()
      const w = LOCATOR_ICON_WIDTH
      const h = LOCATOR_ICON_HEIGHT
      return L.icon({
        iconUrl: getLocatorIconUrl(colorHex),
        iconSize: [w, h],
        iconAnchor: [w / 2, h],
        popupAnchor: [0, -h],
        className: 'activity-locator-leaflet-icon',
      })
    },
    createUserLocationMarkerIcon() {
      const L = this.getL()
      const size = USER_LOCATOR_ICON_SIZE
      return L.icon({
        iconUrl: getLocatorIconUrl(SELF_LOCATOR_COLOR),
        iconSize: [size, size],
        iconAnchor: [size / 2, size],
        popupAnchor: [0, -size],
        className: 'activity-locator-leaflet-icon activity-locator-leaflet-icon--self',
      })
    },
    addUserLocationMarker(lat, lng) {
      const L = this.getL()
      if (!L || !this.userLocationLayer) return

      this.userLocationLayer.clearLayers()

      const marker = L.marker([lat, lng], {
        icon: this.createUserLocationMarkerIcon(),
        zIndexOffset: 1000,
      })

      marker.bindPopup(`
        <div dir="rtl">
          <strong>موقعك الحالي</strong><br>
          <small>${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}</small>
        </div>
      `)

      marker.addTo(this.userLocationLayer)
    },
    getMarkerColor(actionAt) {
      const minutes = this.getMinutesSince(actionAt)
      if (minutes <= 10) return '#28a745'
      if (minutes <= 20) return '#ffc107'
      if (minutes <= 30) return '#fd7e14'
      return '#dc3545'
    },
    getBadgeVariant(actionAt) {
      const minutes = this.getMinutesSince(actionAt)
      if (minutes <= 10) return 'success'
      if (minutes <= 20) return 'warning'
      if (minutes <= 30) return 'warning'
      return 'danger'
    },
    getMinutesSince(date) {
      if (!date) return 999
      return Math.floor((Date.now() - new Date(date).getTime()) / 60000)
    },
    getPositionUserName(position) {
      if (position.user && position.user.name) {
        return position.user.name
      }
      return `مستخدم #${position.user_id}`
    },
    getActionLabel(action) {
      const labels = {
        created: 'إنشاء',
        updated: 'تحديث',
        deleted: 'حذف',
        restored: 'استعادة',
      }
      return labels[action] || action || '-'
    },
    formatRelativeTime(date) {
      if (!date) return '-'
      const diffMs = Date.now() - new Date(date).getTime()
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) return 'الآن'
      if (diffMins < 60) return `منذ ${diffMins} دقيقة`
      if (diffHours < 24) return `منذ ${diffHours} ساعة`
      if (diffDays < 7) return `منذ ${diffDays} يوم`

      return new Date(date).toLocaleString('ar-EG', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    formatFilteredFrom(date) {
      if (!date) return '-'
      return new Date(date).toLocaleString('ar-EG', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    escapeHtml(text) {
      if (!text) return ''
      return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
    },
    onFiltersChange() {
      this.fetchPositions()
    },
    onUserFilterChange() {
      this.fetchPositions()
    },
    onHistoricalDataChange() {
      if (this.historicalData) {
        this.stopPolling()
      } else {
        this.startPolling()
      }
      this.fetchPositions()
    },
    clearErrorMessage() {
      this.errorMessage = null
    },
    startPolling() {
      this.stopPolling()
      this.pollTimer = setInterval(() => {
        if (!this.loading) {
          this.fetchPositions()
        }
      }, POLL_INTERVAL_MS)
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.user-activity-map-view {
  .map-container-wrapper {
    position: relative;
    border-radius: 0.428rem;
    overflow: hidden;
    border: 1px solid #ebe9f1;
  }

  .activity-leaflet-map {
    width: 100%;
    height: 520px;
    z-index: 1;
  }

  .activity-leaflet-map :deep(.activity-locator-leaflet-icon) {
    background: transparent !important;
    border: none !important;
  }

  .loading-overlay {
    position: absolute;
    inset: 0;
    z-index: 10;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.45);
  }

  .map-legend {
    position: absolute;
    bottom: 12px;
    left: 12px;
    z-index: 500;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 6px;
    padding: 8px 12px;
    font-size: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  .legend-title {
    font-weight: 600;
    margin-bottom: 4px;
  }

  .legend-item {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 2px;
  }

  .legend-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .cursor-pointer {
    cursor: pointer;
  }

  .ip-address-cell {
    font-size: 0.85rem;
  }

  .positions-summary :deep(tbody tr) {
    cursor: pointer;
  }

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
}

::v-deep .activity-marker-popup {
  min-width: 180px;
  line-height: 1.5;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
